/**
 * analytics.js — GRANULLO_COMMERCIAL_INSTRUMENTATION_V1
 *
 * Silent conversion intelligence layer.
 * No CRM semantics. No user complexity.
 * Only structural event telemetry for commercial readiness.
 *
 * Events are fire-and-forget. Never block UI. Never fail visibly.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const queue = [];
let flushTimer = null;
const FLUSH_INTERVAL = 5000;
const FLUSH_SIZE = 10;

function enqueue(event) {
  queue.push({
    ...event,
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  });

  if (queue.length >= FLUSH_SIZE) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL);
  }
}

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const batch = queue.splice(0, queue.length);
  try {
    await fetch(`${API_URL}/api/analytics/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // Silent failure — never break UX for analytics
  }
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

/**
 * Public API — all fire-and-forget
 */
export const analytics = {
  /** Estimator PDF downloaded */
  estimatorPdfDownload: (programType) =>
    enqueue({ event: 'estimator.pdf_download', data: { programType } }),

  /** Partner certification harness triggered */
  partnerCertificationRun: (partnerName) =>
    enqueue({ event: 'partners.certification_run', data: { partnerName } }),

  /** Partner certification passed */
  partnerCertificationPass: (partnerName) =>
    enqueue({ event: 'partners.certification_pass', data: { partnerName } }),

  /** Pricing FAQ opened */
  pricingFaqOpen: (questionIndex) =>
    enqueue({ event: 'pricing.faq_open', data: { questionIndex } }),

  /** Pricing → Estimator funnel */
  pricingToEstimator: () =>
    enqueue({ event: 'pricing.to_estimator', data: {} }),

  /** Partners → Simulation → Certification funnel */
  partnersFunnelStep: (step) =>
    enqueue({ event: 'partners.funnel_step', data: { step } }),

  /** Board memo copy CTA */
  boardMemoCopy: () =>
    enqueue({ event: 'estimator.board_memo_copy', data: {} }),

  /** Top-nav CTA click attribution */
  navCtaClick: (cta, fromRoute) =>
    enqueue({ event: 'nav.cta_click', data: { cta, fromRoute } }),

  /** Pricing interaction (tier hover, formula view) */
  pricingInteraction: (action, detail) =>
    enqueue({ event: 'pricing.interaction', data: { action, detail } }),

  /** Route completion (full scroll) */
  routeCompletion: (route) =>
    enqueue({ event: 'route.completion', data: { route } }),

  /** Generic event for extensibility */
  track: (event, data = {}) =>
    enqueue({ event, data }),
};
